import { action, observable } from "mobx";

import apiCaller from "@client/utils/ApiCaller";
import { Customer, CustomerInput } from "@typings/Customer";

class CustomerStore {
  @observable loading = false;
  @observable updating = false;
  @observable customers: Customer[] = [];

  @action async load(opts?: {
    forceShowLoading?: boolean;
    hideLoading?: boolean;
  }): Promise<void> {
    this.loading = this.customers.length === 0;

    if (opts) {
      if (opts.hideLoading) this.loading = false;
      if (opts.forceShowLoading) this.loading = true;
    }
    try {
      const customers: Customer[] = await apiCaller.send({ url: "/customers" });
      this.loadSuccess(customers);
    } catch (err) {
      this.loadFailed();
    }
  }

  @action loadSuccess(customers: Customer[]) {
    this.loading = false;
    this.customers = customers;
  }

  @action loadFailed() {
    this.loading = false;
  }

  @action async add(customer: CustomerInput): Promise<Customer | null> {
    this.updating = true;
    try {
      const addedCustomer: Customer = await apiCaller.send({
        url: "/customers",
        method: "POST",
        data: customer,
      });
      this.addSuccess(addedCustomer);
      return addedCustomer;
    } catch (err) {
      this.addFailed();
      return null;
    }
  }

  @action addSuccess(customer: Customer) {
    this.updating = false;
    const customers = [...this.customers, customer];
    customers.sort((a, b) => a.name.localeCompare(b.name));
    this.customers = [...customers];
  }

  @action addFailed() {
    this.updating = false;
  }

  @action async update(
    customerId: string,
    customer: CustomerInput,
  ): Promise<Customer | null> {
    this.updating = true;
    try {
      const updatedCustomer: Customer = await apiCaller.send({
        url: `/customers/${customerId}`,
        method: "PUT",
        data: customer,
      });
      this.updateSuccess(updatedCustomer);
      return updatedCustomer;
    } catch (err) {
      this.updateFailed();
      return null;
    }
  }

  @action updateSuccess(customer: Customer) {
    const customers = this.customers.filter(c => c.id !== customer.id);
    customers.push({ ...customer });
    customers.sort((a, b) => a.name.localeCompare(b.name));
    this.customers = [...customers];
    this.updating = false;
  }

  @action updateFailed() {
    this.updating = false;
  }

  async delete(customerId: string) {
    await apiCaller.send({
      url: `/customers/${customerId}`,
      method: "DELETE",
    });
    this.deleteSuccess(customerId);
  }

  @action deleteSuccess(customerId: string) {
    this.customers = this.customers.filter(
      customer => customer.id !== customerId,
    );
  }
}

export default new CustomerStore();
